import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { Client } from '../../../api/app-types/client.app';
import { ContactType } from '../../../api/app-types/contact-type';
import { ContactValidDateRange, CreateContactBody } from '../../../api/app-types/contact.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../api/app-types/document.app';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { SearchResultItem } from '../../../api/app-types/search.app';
import { User } from '../../../api/app-types/user.app';
import { CONTACT_API } from '../../../api/services/contact.service';
import { UPLOAD_API, UploadApi } from '../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';
import { SearchableTaskPicker } from '../../../core/components/searchable-task-picker/searchable-task-picker';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { STATE } from '../../../core/services/state.service';
import { DocumentUploadControl } from '../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../shared/document-upload/document-upload-validators';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import {
	formatDateToISOString,
	matDateProviders,
	normalizeToEndOfDay,
	normalizeToStartOfDay,
	parseDate,
} from '../../../shared/helpers/date.func';
import { hasError, hasFileError } from '../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../shared/validators/max-date-validator';
import { AssignmentViewQueryParams } from '../../client/client-card/client-card';
import { Participants } from '../participants/participants';

@Component({
	selector: 'lvr-create-contact',
	imports: [
		TranslocoPipe,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatButtonModule,
		LvrInputDirective,
		LvrAutoSaveDirective,
		LvrAppendAsteriskPipe,
		LvrRestoreActions,
		Participants,
		LvrPaddingDirective,
		LvrField,
		LvrHeading,
		LvrSearchableSelect,
		DocumentUploadControl,
		DatePipe,
		SearchableTaskPicker,
		LvrDefaultActions,
	],
	templateUrl: './create-contact.html',
	styleUrl: './create-contact.scss',
	providers: [
		matDateProviders,
		{
			provide: UPLOAD_API,
			useClass: UploadApi,
		},
	],
})
export class CreateContact extends LvrRedirectNav {
	public readonly compareById = compareById;
	private readonly assignment = this.resolveDataAsSignal<AssignmentWithRelations>('assignment');
	public readonly subjectType = DocumentFileSubjectType.CONTACT;
	public readonly env = inject(ENVIRONMENT);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly keywordCtrlMaxLength = this.env.textInputMaxLength;
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly dateRange = this.resolveDataAsSignal<ContactValidDateRange>('dateRange');
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly isLoading = signal<boolean>(false);

	public readonly minDate = computed(() => {
		const dateRangeMin = this.dateRange()?.minStartDate;
		const minDateParsed = parseDate(dateRangeMin);
		return minDateParsed ? normalizeToStartOfDay(minDateParsed) : undefined;
	});

	public readonly maxDate = computed(() => {
		const date = this.dateRange()?.maxEndDate;
		if (!date) return undefined;

		return normalizeToEndOfDay(date);
	});

	public readonly form = this.initForm(inject(FormBuilder));
	public readonly contactTypeSelectControl = new FormControl<number | undefined>(undefined, {
		nonNullable: true,
		validators: [Validators.required],
	});
	public readonly contactTypeOptionValue = (type: ContactType): number => type.id;
	public readonly contactTypeOptionLabel = (type: ContactType): string => type.remark ?? '';

	private readonly api = inject(CONTACT_API);
	private readonly draftService = inject(FormPersistenceService);
	private readonly ntfySvc = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly state = inject(STATE);

	private readonly contactTypesPage = this.resolveDataAsSignal<PaginatedResponse<ContactType>>('contactTypes');
	private readonly resolvedTypeOfCareId = this.resolveDataAsSignal<number | undefined>('typeOfCareId');

	public readonly contactTypes = computed(() => this.contactTypesPage()?.entities);
	public readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly user = this.resolveDataAsSignal<User>('user');
	public readonly clientFullName = computed(() => `${this.client().firstName} ${this.client().name}`);

	public readonly phaseId = signal<number>(0);
	public readonly typeOfCareId = signal<number | null>(null);
	private readonly isAdditionalAssignment = signal<boolean>(false);
	private readonly assignmentIdForReset = signal<number | null>(null);
	public readonly isTasksPhase = computed(() => this.isAdditionalAssignment() || this.isP2orP3(this.phaseId()));
	public readonly draftId = computed(() => `${FORM_KEYS.CREATE_CONTACT}-${this.client().id}`);
	public readonly canCreateContactsPermission = hasPermission(this.session.permissions, [
		'Contacts.Create',
		'Contacts.Create.PhaseOneOnly',
	]);
	public readonly canManageContactDocuments = hasPermission(this.session.permissions, 'Documents.Contact.Create');
	public readonly canReadTasks = hasPermission(this.session.permissions, 'Tasks.Read');
	public readonly canCreateTasks = hasPermission(this.session.permissions, 'Tasks.Create');

	private readonly participantsRef = viewChild(Participants);
	/**
	 * True while the "client participated" toggle is still fetching the client via global search.
	 * Saving must be blocked until this settles, otherwise the client can be missing from
	 * `participants` even though the checkbox appears checked.
	 */
	public readonly isClientLookupPending = computed(() => this.participantsRef()?.isClientLookupPending() ?? false);

	public constructor() {
		super();

		const contactTypeControl = this.form.controls.contactType;
		contactTypeControl.valueChanges.subscribe((type) => {
			const typeId = type?.id;
			if (this.contactTypeSelectControl.value !== typeId) {
				this.contactTypeSelectControl.setValue(typeId, { emitEvent: false });
			}
		});
		this.contactTypeSelectControl.valueChanges.subscribe((typeId) => {
			const selectedType = typeId === undefined ? undefined : this.contactTypes()?.find((type) => type.id === typeId);
			if (contactTypeControl.value?.id !== typeId) {
				contactTypeControl.setValue(selectedType, { emitEvent: false });
			}
		});
		const initialContactType = contactTypeControl.value;
		this.contactTypeSelectControl.setValue(initialContactType?.id, { emitEvent: false });

		effect(() => {
			const a = this.assignment();
			if (!a) return;
			const currentTypeOfCareId = a.typeOfCareId ?? a.typeOfCare?.id ?? null;
			const tasksTypeOfCareId = a.isAdditionalAssignment
				? (this.resolvedTypeOfCareId() ?? currentTypeOfCareId)
				: (currentTypeOfCareId ?? this.resolvedTypeOfCareId() ?? null);

			// Reset only when switching to another assignment, not on late resolver updates.
			if (this.assignmentIdForReset() !== a.id) {
				this.form.reset();
				this.assignmentIdForReset.set(a.id);
			}

			// Additional assignments uses P3 for tasks in assignments
			const pId = a.isAdditionalAssignment ? DB_PHASES.P3 : a.phaseId;
			this.phaseId.set(pId);
			this.typeOfCareId.set(tasksTypeOfCareId);
			this.isAdditionalAssignment.set(!!a.isAdditionalAssignment);
		});

		// Enables or disables the tasks picker according to the Phase
		effect(() => {
			const allowed = this.isTasksPhase() && this.canReadTasks() && this.canCreateTasks();
			const ctrl = this.form.controls.selectedTasksIds;
			if (allowed) {
				ctrl.enable({ emitEvent: false });
			} else {
				ctrl.setValue([], { emitEvent: false });
				ctrl.disable({ emitEvent: false });
			}
		});
	}

	/**
	 * Cancel new contact creation.
	 */
	public cancel = () => {
		this.draftService.deleteDraft('contact', this.draftId());
		this.navigateAfterFinished(['../../../'], { relativeTo: this.route });
	};

	/**
	 * Checks if documents form control has a specific validation error and has been touched.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if any error exists and the control was touched.
	 */
	public hasFileError = (control: AbstractControl | null, ...errors: string[]) => hasFileError(control, ...errors);

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	/**
	 * Create a new contact.
	 */
	public createContact = async () => {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		// Guards against Enter/submit or a double click bypassing the disabled save button
		// while the client lookup triggered by the participants toggle is still in flight.
		if (this.isClientLookupPending()) {
			return;
		}

		const fv = this.form.getRawValue();
		const docs = (fv.documents ?? []) as DocumentFile[];

		const body: CreateContactBody = {
			anonym: fv.anonym ?? false,
			confidential: fv.confidential ?? false,
			contactedAt: formatDateToISOString(fv.contactedAt) ?? '',
			contactType: fv.contactType!,
			keyword: fv.keyword?.trim() ?? '',
			remark: fv.remark?.trim() ?? '',
			participants: (fv.participants ?? []).map((p: SearchResultItem) => ({
				id: p.item.id,
				typeId: p.personType,
			})),
			documentIds: docs.map((d: DocumentFile) => d.id),
			selectedTasksIds: fv.selectedTasksIds ?? [],
		};

		const queryParams = this.buildAssignmentQueryParams();
		if (!queryParams) return;

		this.isLoading.set(true);

		try {
			await this.api.create(this.client().id, this.assignment()!.id, body);
			await this.state.updateAssignments();
			await this.navigateAfterFinished(['/desk/client', this.client().id, 'assignments'], { queryParams });
			this.draftService.deleteDraft('contact', this.draftId());
			this.ntfySvc.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.contact_created')),
			);
		} catch {
			this.ntfySvc.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.contact_create_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	};

	/**
	 * Initializes the form for creating a new contact.
	 */
	private initForm(fb: FormBuilder) {
		return fb.group({
			anonym: fb.control<boolean>(false),
			remark: fb.control<string>('', [Validators.maxLength(this.remarkCtrlMaxLength)]),
			confidential: fb.control<boolean>(false),
			keyword: fb.control<string>('', [Validators.required]),
			contactType: fb.control<ContactType | undefined>(undefined, [Validators.required]),
			contactedAt: fb.control<Date | undefined>(undefined, [
				Validators.required,
				minDateValidator(this.minDate()),
				maxDateValidator(this.maxDate()),
			]),
			participants: fb.control<SearchResultItem[]>([]),
			documents: fb.control<DocumentFile[]>(
				[],
				[
					maxFiles(this.env.contactSettings.maxFiles),
					maxFileSize(this.env.fileSettings.maxFileSize),
					allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
				],
			),
			selectedTasksIds: fb.control<number[]>([]),
		});
	}

	private isP2orP3 = (p?: number | null) => p === DB_PHASES.P2 || p === DB_PHASES.P3;

	private buildAssignmentQueryParams(): AssignmentViewQueryParams | null {
		const assignment = this.assignment();
		const client = this.client();

		if (!assignment?.id) return null;

		if (!assignment.isAdditionalAssignment) {
			return {
				displayName: `${client.firstName} ${client.name}`,
				openMain: assignment.id,
			};
		}

		const mainAssignment = this.state
			.currentAssignments()
			.find((a) => !a.isAdditionalAssignment && !a.isServiceOfficeStatement);

		if (!mainAssignment?.id) return null;

		return {
			displayName: `${client.firstName} ${client.name}`,
			openMain: mainAssignment.id,
			openChild: assignment.id,
		};
	}
}
